import { Alert, Form } from "antd-v5";
import { BaseModalRef } from "components/BaseModal";
import BaseModalForm from "components/BaseModalForm";
import message from "components/CustomMessage";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import _ from "lodash";
import React, { forwardRef, useCallback, useMemo, useRef } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, urlRegexValidator } from "utils/formUtils";
import ModalUtil from "utils/modalUtil";

import AutoMappingSettingsForm, {
  GIT_TOOL_MAPPING_CONFIG,
  Values,
} from "../common/AutoMappingSettingsForm";

type ConfigDTO = {
  subProductMappingConfig: Array<GIT_TOOL_MAPPING_CONFIG>;
  authType: string;
  codeClassificationEnabled: boolean;
  githubIssuesEnabled?: boolean;
  ingestComponents?: boolean;
  // autoCreateGitToolConfig: Array<AUTO_CREATE_GIT_TOOL_CONFIG>,
};

type Props = {
  configID: string;
  afterClose?: () => void;
  initialValues: ConfigDTO;
  hostUrl: string;
  toolName?: string;
};

export default forwardRef<BaseModalRef, Props>(function GitHubSettingsFormPopup(
  { configID, afterClose, initialValues, hostUrl, toolName },
  ref,
) {
  const toolConfigs = useRef<Values>();
  const featureFlags = useFeatureFlag();
  const [form] = Form.useForm();
  const isEditForPAT = initialValues?.authType === "TOKEN";

  const initialConfig = useMemo(() => {
    const config = {} as Values;
    _.forEach(
      initialValues?.subProductMappingConfig,
      (entry) => (config[entry] = true),
    );
    config.codeClassificationEnabled =
      !!initialValues?.codeClassificationEnabled;
    // _.forEach(initialValues?.autoCreateGitToolConfig, entry => config[entry] = true);
    config.githubIssuesEnabled = initialValues?.githubIssuesEnabled ?? false;
    config.ingestComponents = initialValues?.ingestComponents ?? false;
    toolConfigs.current = config;
    return config;
  }, [initialValues]);

  const saveSettings = useCallback(
    async (id) => {
      const configs = toolConfigs.current;
      const payload = {
        subProductMappingConfig: featureFlags.SIMPLIFIED_ONBOARDING
          ? _.filter(
              [
                GIT_TOOL_MAPPING_CONFIG.MAP_INACTIVE_REPOS,
                GIT_TOOL_MAPPING_CONFIG.MAP_DORMANT_REPOS,
              ],
              (entry) => configs?.[entry],
            )
          : [],
        codeClassificationEnabled: configs?.codeClassificationEnabled,
        githubIssuesEnabled: configs?.githubIssuesEnabled,
        ingestComponents: configs?.ingestComponents,
      };

      await ToolsIntegrationsAPI.getGithubInstallationConfigs(id, payload)
        .promise;
      ModalUtil.closeAll();
    },
    [featureFlags.SIMPLIFIED_ONBOARDING],
  );

  const submitData = useCallback(async () => {
    const values = form.getFieldsValue();

    if (isEditForPAT) {
      const dataToSend = {
        ...values,
        id: configID,
        repoType: "GITHUB",
        token: values.token,
      };
      await ToolsIntegrationsAPI.updateGitInstallation(dataToSend).promise;
    }

    await saveSettings(configID);

    message.success(`Successfully updated.`);
  }, [form, isEditForPAT, configID, saveSettings]);

  const onSettingsChange = useCallback((changedSetting, settings) => {
    toolConfigs.current = settings;
  }, []);

  const baseModalProps = {
    okText: "Configure",
  };

  const GithubPATFields = useMemo(
    () => [
      ...(isEditForPAT
        ? [
            {
              name: "hostUrl",
              label: "Host Url",
              rules: [urlRegexValidator()],
              colSpan: 24,
              required: true,
              disabled: isEditForPAT,
            },
            {
              colSpan: 24,
              render: () => (
                <Alert
                  className="m-b-m"
                  message="Please ensure the new token has required permissions to existing repositories present in ArmorCode. 
          Adding token with incorrect permissions would result in deletion of repositories from ArmorCode."
                  type="warning"
                />
              ),
            },
            {
              name: "token",
              label: "Token",
              type: FieldTypes.PASSWORD,
              help: "Enter a valid token or Generate it",
              colSpan: 24,
              required: true,
            },
          ]
        : []),
    ],
    [isEditForPAT],
  );

  return (
    <BaseModalForm
      modalProps={baseModalProps}
      ref={ref}
      //@ts-expect-error : version mismatch for form
      form={form}
      fields={GithubPATFields}
      onOk={submitData}
      afterClose={afterClose}
      title={`Configure GitHub`}
      initialValues={{ hostUrl: hostUrl }}
    >
      <AutoMappingSettingsForm
        onChange={onSettingsChange}
        values={initialConfig}
        toolName={toolName}
      />
    </BaseModalForm>
  );
});
